import { ReactElement } from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colors } from '~/styles/colors';
import {
  Card, FieldColumn, FieldLabel, mobileQuery, TwoColumns,
} from '../ds';
import { validateRequiredNumber } from '../helpers';
import { DamEcoSimplifiedFormValues } from '../types';
import { AdvancedOptions } from './AdvancedOptions';
import { AdvancedIllustrations, EcoDayPanel } from './illustrations/EcoIllustrations';
import { InfoHint, Toggle } from './controls';
import { GlobeIcon } from './icons';
import { StepperField } from './StepperField';

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${mobileQuery} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const HeaderMain = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  min-width: 0;

  ${mobileQuery} {
    flex-basis: 100%;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${colors.brand.primary.base};
`;

const GlobalPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border: 1px solid ${colors.stroke['soft-200']};
  border-radius: 999px;
  background: ${colors.LightGrey};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${colors.text['sub-500']};
`;

const HeaderToggle = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const StateLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  color: ${colors.text['main-900']};
`;

const Body = styled.div`
  margin-top: 22px;
`;

const ToggleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 48px;
  padding: 0 16px;
  border: 1px solid ${colors.stroke['soft-200']};
  border-radius: 8px;
  background: ${colors.bg['white-0']};
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text['main-900']};
`;

const BODY_FIELDS: (keyof DamEcoSimplifiedFormValues)[] = ['setpoint', 'upperHysteresis', 'lowerHysteresis', 'lti', 'stageAddDelay', 'stageReduceDelay'];

interface EcoModeSectionProps {
  readOnly: boolean;
}

export function EcoModeSection({ readOnly }: Readonly<EcoModeSectionProps>): ReactElement {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<DamEcoSimplifiedFormValues>();
  const { errors } = useFormState({ control });
  const enableEco = watch('enableEco');

  // Com o Modo ECO desligado o corpo fica oculto, como no design, a menos que haja campo a corrigir.
  const isBodyVisible = enableEco || BODY_FIELDS.some((field) => !!errors[field]);
  const stateLabel = (on: boolean): string => t(on ? 'ecoSimplificadoAtivado' : 'ecoSimplificadoDesativado');

  return (
    <Card data-testid="eco-simplificado-modo-eco">
      <Header>
        <HeaderMain>
          <Title>{t('modoEco')}</Title>
          <GlobalPill>
            <GlobeIcon size={15} color={colors.brand.primary.base} />
            {t('ecoSimplificadoConfiguracaoGlobal')}
          </GlobalPill>
        </HeaderMain>
        <Controller
          control={control}
          name="enableEco"
          render={({ field }) => (
            <HeaderToggle>
              <StateLabel>{stateLabel(field.value)}</StateLabel>
              <Toggle checked={field.value} onChange={field.onChange} label={t('modoEco')} disabled={readOnly} testId="eco-simplificado-enableEco" />
            </HeaderToggle>
          )}
        />
      </Header>

      <Body hidden={!isBodyVisible}>
        <TwoColumns $left="minmax(0, 340px)">
          <FieldColumn>
            <StepperField
              name="setpoint"
              label={t('ecoSimplificadoSetpoint')}
              unit="°C"
              validate={validateRequiredNumber}
              disabled={readOnly}
              extra={{ info: t('ecoSimplificadoSetpointDica') }}
            />
            <div>
              <FieldLabel>
                {t('ecoLocal')}
                <InfoHint id="eco-simplificado-eco-local-info" text={t('ecoSimplificadoEcoLocalDica')} />
              </FieldLabel>
              <Controller
                control={control}
                name="enableEcoLocal"
                render={({ field }) => (
                  <ToggleBox>
                    {stateLabel(field.value)}
                    <Toggle checked={field.value} onChange={field.onChange} label={t('ecoLocal')} disabled={readOnly} testId="eco-simplificado-enableEcoLocal" />
                  </ToggleBox>
                )}
              />
            </div>
          </FieldColumn>
          <EcoDayPanel />
        </TwoColumns>
        <AdvancedOptions readOnly={readOnly} illustrations={<AdvancedIllustrations />} />
      </Body>
    </Card>
  );
}
